import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { formatDistanceToNow } from 'date-fns'
import { Bot } from 'lucide-react'
import { ExceptionDetailDialog } from './exception-detail-dialog'
import { ExceptionStatusBadge } from './status-badge'
import type { AppException } from '@/types/app-exceptions'

interface ExceptionsTableProps {
  exceptions: AppException[]
  statusFilter: string
  onStatusFilterChange: (status: string) => void
  onResolve: (id: number) => Promise<void>
  onIgnore: (id: number) => Promise<void>
  onAnalyzeAndFix: (id: number) => Promise<void>
  loading?: boolean
}

export function ExceptionsTable({
  exceptions,
  statusFilter,
  onStatusFilterChange,
  onResolve,
  onIgnore,
  onAnalyzeAndFix,
  loading = false,
}: ExceptionsTableProps) {
  const { t } = useTranslation('conversations')
  const [selectedExc, setSelectedExc] = useState<AppException | null>(null)

  const filters = ['unresolved', 'resolved', 'ignored'] as const

  return (
    <div className="space-y-3">
      {/* Filter chips */}
      <div className="flex gap-2">
        {filters.map(status => (
          <Button
            key={status}
            size="sm"
            variant={statusFilter === status ? 'default' : 'outline'}
            onClick={() => onStatusFilterChange(status)}
          >
            {t(`errors.status.${status}`)}
          </Button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-8 text-muted-foreground text-sm">
          {t('errors.loading')}
        </div>
      ) : exceptions.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground text-sm">
          {t('errors.empty')}
        </div>
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">{t('errors.columns.exception')}</th>
                <th className="text-left p-3 font-medium">{t('errors.columns.location')}</th>
                <th className="text-right p-3 font-medium">{t('errors.columns.count')}</th>
                <th className="text-left p-3 font-medium">{t('errors.columns.lastSeen')}</th>
                <th className="text-left p-3 font-medium">{t('errors.columns.status')}</th>
              </tr>
            </thead>
            <tbody>
              {exceptions.map(exc => (
                <tr
                  key={exc.id}
                  className="border-t cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => setSelectedExc(exc)}
                >
                  <td className="p-3">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-destructive">{exc.exception_class}</span>
                      {exc.severity && (
                        <Badge variant={exc.severity === 'p0' ? 'destructive' : exc.severity === 'p1' ? 'default' : 'secondary'} className="text-[10px] px-1 py-0">
                          {exc.severity.toUpperCase()}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate max-w-xs">{exc.message}</div>
                  </td>
                  <td className="p-3">
                    {exc.location && (
                      <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{exc.location}</code>
                    )}
                  </td>
                  <td className="p-3 text-right font-mono">{exc.occurrence_count}</td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(exc.last_seen_at), { addSuffix: true })}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1.5">
                      <ExceptionStatusBadge status={exc.status} />
                      {exc.resolved_by_pilot && <Bot className="h-3.5 w-3.5 text-muted-foreground" />}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ExceptionDetailDialog
        exception={selectedExc}
        open={selectedExc !== null}
        onOpenChange={(open) => { if (!open) setSelectedExc(null) }}
        onResolve={onResolve}
        onIgnore={onIgnore}
        onAnalyzeAndFix={onAnalyzeAndFix}
      />
    </div>
  )
}
